/**
 * Shared Web Audio primitives for locally generated tones: the DTMF keypad
 * feedback (dtmfTone) and the chat-message notification chime
 * (chatMessageTone). Everything is synthesized in the browser — no audio
 * assets. Both callers share one AudioContext; browsers cap the number of
 * contexts, so lazily creating one per tone source could exhaust the quota.
 */

let audioContext: AudioContext | null = null;

/** Lazily created shared AudioContext; null when audio is unavailable. */
export function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (
      window as unknown as {
        webkitAudioContext?: typeof AudioContext;
      }
    ).webkitAudioContext;
  if (!Ctor) return null;
  audioContext ??= new Ctor();
  return audioContext;
}

export type ToneBurst = {
  /** Sine frequency in Hz. */
  frequency: number;
  /** Absolute start time on the context clock. */
  startTime: number;
  durationSeconds: number;
  gain: number;
};

/** Plays a short sine burst with a quick exponential fade-out. */
export function playToneBurst(ctx: AudioContext, burst: ToneBurst): void {
  // Autoplay policies require resume() from a user gesture; tone is best-effort.
  void ctx.resume().catch(() => {});
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.value = burst.frequency;
  gainNode.gain.setValueAtTime(burst.gain, burst.startTime);
  gainNode.gain.exponentialRampToValueAtTime(
    0.0001,
    burst.startTime + burst.durationSeconds
  );
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.start(burst.startTime);
  oscillator.stop(burst.startTime + burst.durationSeconds + 0.02);
}
